import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Hotel, QrCode, Phone, Loader2, CheckCircle2, 
  ArrowRight, KeyRound, Send, RefreshCw, Mail, MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import PhoneInput from '@/components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SelfCheckInLanding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('code');
  
  // Code entry state
  const [bookingCode, setBookingCode] = useState('');
  const [codeOtpSent, setCodeOtpSent] = useState(false);
  const [codeLookupResult, setCodeLookupResult] = useState(null);
  
  // Phone entry state
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [lookupResult, setLookupResult] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  // Handle booking code lookup - now requires OTP
  const handleCodeLookup = async (e) => {
    e.preventDefault();
    if (!bookingCode.trim()) {
      toast.error('Please enter your booking code');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/lookup`, {
        booking_code: bookingCode.trim().toUpperCase()
      });

      if (res.data.found) {
        setCodeLookupResult(res.data);
        
        // Now send OTP
        await sendCodeOTP(bookingCode.trim().toUpperCase());
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid booking code');
    } finally {
      setLoading(false);
    }
  };

  // Send OTP for booking code
  const sendCodeOTP = async (code) => {
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/send-otp`, {
        booking_code: code,
        channel: 'all'
      });

      setCodeOtpSent(true);
      setSentTo(res.data.sent_to);
      
      if (res.data.demo_mode) {
        toast.info(`OTP sent! Hint: ${res.data.otp_hint}`, { duration: 10000 });
      } else {
        toast.success('OTP sent to your registered contact');
      }

      // Start resend timer
      startResendTimer();

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Verify OTP for booking code
  const handleCodeVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/verify-otp`, {
        booking_code: bookingCode.trim().toUpperCase(),
        otp: otp.trim()
      });

      if (res.data.success) {
        toast.success('OTP verified! Redirecting...');
        // Include verification token in URL for security
        const token = res.data.verification_token;
        if (token) {
          navigate(`/self-checkin/${res.data.checkin_code}?token=${token}`);
        } else {
          navigate(`/self-checkin/${res.data.checkin_code}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Handle phone lookup
  const handlePhoneLookup = async (e) => {
    e.preventDefault();
    if (!phone.trim() || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/lookup`, {
        phone: phone.trim()
      });

      if (res.data.found) {
        setLookupResult(res.data);
        // Send OTP
        await sendOTP();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No booking found for this number');
    } finally {
      setLoading(false);
    }
  };

  // Send OTP for phone
  const sendOTP = async () => {
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/send-otp`, {
        phone: phone.trim(),
        channel: 'all'  // Send via SMS, WhatsApp, and Email
      });

      setOtpSent(true);
      setSentTo(res.data.sent_to);
      
      if (res.data.demo_mode) {
        toast.info(`OTP sent! Hint: ${res.data.otp_hint}`, { duration: 10000 });
      } else {
        toast.success('OTP sent to your registered contact');
      }

      // Start resend timer
      startResendTimer();

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/self-checkin/verify-otp`, {
        phone: phone.trim(),
        otp: otp.trim()
      });

      if (res.data.success) {
        toast.success('OTP verified! Redirecting...');
        // Include verification token in URL for security
        const token = res.data.verification_token;
        if (token) {
          navigate(`/self-checkin/${res.data.checkin_code}?token=${token}`);
        } else {
          navigate(`/self-checkin/${res.data.checkin_code}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    await sendOTP();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Hotel className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Self Check-in</h1>
          <p className="text-gray-500 mt-2">Quick & contactless check-in experience</p>
        </div>

        {/* Main Card */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center">How would you like to check in?</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="code" className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  Booking Code
                </TabsTrigger>
                <TabsTrigger value="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </TabsTrigger>
              </TabsList>

              {/* Booking Code Tab */}
              <TabsContent value="code">
                {!codeOtpSent ? (
                  // Code Entry Step
                  <form onSubmit={handleCodeLookup} className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-600">Enter your booking code</Label>
                      <Input
                        value={bookingCode}
                        onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
                        placeholder="e.g., ABC12345"
                        className="mt-1 text-center text-lg tracking-widest font-mono uppercase"
                        maxLength={10}
                        data-testid="booking-code-input"
                      />
                      <p className="text-xs text-gray-400 mt-1 text-center">
                        You can find this code in your booking confirmation
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading || !bookingCode.trim()}
                      className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      data-testid="code-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  // OTP Verification Step for Booking Code
                  <form onSubmit={handleCodeVerifyOTP} className="space-y-4">
                    {codeLookupResult && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-700">Booking Found!</p>
                            <p className="text-xs text-green-600">
                              {codeLookupResult.hotel_name} - {codeLookupResult.guest_name}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show where OTP was sent */}
                    {sentTo && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium text-blue-700">OTP sent to:</p>
                        <div className="flex flex-wrap gap-2">
                          {sentTo.phone && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              <Phone className="h-3 w-3" /> {sentTo.phone}
                            </span>
                          )}
                          {sentTo.email && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              <Mail className="h-3 w-3" /> {sentTo.email}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm text-gray-600">Enter the OTP</Label>
                      <Input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        data-testid="code-otp-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading || otp.length !== 6}
                      className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      data-testid="code-otp-verify-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <KeyRound className="h-5 w-5 mr-2" />
                          Verify & Continue
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          setCodeOtpSent(false);
                          setOtp('');
                          setCodeLookupResult(null);
                          setSentTo(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ← Change Code
                      </button>
                      <button
                        type="button"
                        onClick={() => sendCodeOTP(bookingCode.trim().toUpperCase())}
                        disabled={resendTimer > 0 || otpLoading}
                        className={`flex items-center gap-1 ${
                          resendTimer > 0 ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-700'
                        }`}
                      >
                        {otpLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>

              {/* Phone Number Tab */}
              <TabsContent value="phone">
                {!otpSent ? (
                  // Phone Input Step
                  <form onSubmit={handlePhoneLookup} className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-600">Enter your registered phone number</Label>
                      <div className="mt-1">
                        <PhoneInput
                          value={phone}
                          onChange={(value) => setPhone(value)}
                          placeholder="Enter phone number"
                          data-testid="self-checkin-phone"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 text-center">
                        We&apos;ll send an OTP to verify your identity
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading || phone.length < 10}
                      className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      data-testid="phone-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Finding Booking...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  // OTP Verification Step
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    {lookupResult && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-700">Booking Found!</p>
                            <p className="text-xs text-green-600">
                              {lookupResult.hotel_name} - {lookupResult.guest_name}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show where OTP was sent */}
                    {sentTo && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium text-blue-700">OTP sent to:</p>
                        <div className="flex flex-wrap gap-2">
                          {sentTo.phone && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              <Phone className="h-3 w-3" /> {sentTo.phone}
                            </span>
                          )}
                          {sentTo.email && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              <Mail className="h-3 w-3" /> {sentTo.email}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm text-gray-600">Enter the OTP</Label>
                      <Input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        data-testid="otp-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading || otp.length !== 6}
                      className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      data-testid="otp-verify-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <KeyRound className="h-5 w-5 mr-2" />
                          Verify & Continue
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setLookupResult(null);
                          setSentTo(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ← Change Number
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={resendTimer > 0 || otpLoading}
                        className={`flex items-center gap-1 ${
                          resendTimer > 0 ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-700'
                        }`}
                      >
                        {otpLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Need help? Contact the front desk
        </p>
      </div>
    </div>
  );
}
